import {
  PropsWithChildren,
  createContext,
  useContext,
  useMemo,
  useReducer,
} from "react";
import React from "react";

import {
  type FilterReducerActions,
  filterMenuReducer,
  userHasSelectedFilters,
} from "./filter-state-reducer";
import type {
  DerivedStates,
  FilterActions,
  MenuFilterState,
} from "./filter-state-type";

type MenuFilterCtx = MenuFilterState & FilterActions & DerivedStates;
const FilterMenuContext = createContext<MenuFilterCtx | null>(null);

type FilterMenuProviderProps = PropsWithChildren<{
  reducer?: (
    state: MenuFilterState,
    action: FilterReducerActions
  ) => MenuFilterState;
  initialState: MenuFilterState;
  initialActions?: FilterReducerActions[]; // used for testing
}>;

export const FilterMenuProvider = ({
  children,
  reducer = filterMenuReducer,
  initialState,
  initialActions = [],
}: FilterMenuProviderProps) => {
  const [state, dispatch] = useReducer(
    reducer,
    initialActions.reduce(reducer, initialState)
  );

  const menuFilterCtx: MenuFilterCtx = useMemo(() => {
    const filterActions: FilterActions = {
      toggleAllergen: (allergen) =>
        dispatch({ type: "toggleAllergen", allergen }),
      togglePreference: (preference) =>
        dispatch({ type: "togglePreference", preference }),
      toggleIngredient: (ingredient) =>
        dispatch({ type: "toggleIngredient", ingredient }),
      toggleTag: (tag) => dispatch({ type: "toggleTag", tag }),
      toggleShowMayContainAllergens: () =>
        dispatch({ type: "toggleShowMayContainAllergens" }),
      toggleShowCalories: () => dispatch({ type: "toggleShowCalories" }),
      toggleAllergenDetails: () => dispatch({ type: "toggleAllergenDetails" }),
      clearAll: () => dispatch({ type: "clearAll" }),
    };

    const derivedStates: DerivedStates = {
      userHasSelectedFilters: () => userHasSelectedFilters(state),
    };

    return { ...state, ...filterActions, ...derivedStates };
  }, [state, dispatch]);

  return (
    <FilterMenuContext.Provider value={menuFilterCtx}>
      {children}
    </FilterMenuContext.Provider>
  );
};

export const useFilterMenu = (): MenuFilterCtx => {
  const ctx = useContext(FilterMenuContext);

  if (!ctx) {
    throw new Error("useFilterMenu must be used within FilterMenuProvider");
  }

  return ctx;
};
